// src/components/ToastProvider.jsx
import React, { createContext, useContext, useState, useCallback } from "react";
import { CheckCircle, AlertTriangle, Info, X } from "lucide-react";

const ToastContext = createContext(null);
export const useToast = () => useContext(ToastContext);

const VARIANTS = {
    success: {
        icon: <CheckCircle size={18} />,
        base: "bg-white border border-emerald-200",
        text: "text-emerald-900",
        pill: "bg-emerald-100 text-emerald-700 border-emerald-200",
    },
    error: {
        icon: <AlertTriangle size={18} />,
        base: "bg-white border border-rose-200",
        text: "text-rose-900",
        pill: "bg-rose-100 text-rose-700 border-rose-200",
    },
    info: {
        icon: <Info size={18} />,
        base: "bg-white border border-sky-200",
        text: "text-sky-900",
        pill: "bg-sky-100 text-sky-700 border-sky-200",
    },
};

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const remove = useCallback((id) => {
        setToasts((t) => t.filter((x) => x.id !== id));
    }, []);

    const toast = useCallback(({ title, message, variant = "info", duration = 3000 }) => {
        const id = `${variant}-${title}-${message}`;
        setToasts((prev) => {
            if (prev.some((t) => t.id === id)) return prev; // ✅ ป้องกันข้อความซ้ำ
            return [...prev, { id, title, message, variant }];
        });
        if (duration) setTimeout(() => remove(id), duration);
    }, [remove]);

    return (
        <ToastContext.Provider value={{ toast }}>
            {children}

            {/* 🔹 Toast Container (Top Center) */}
            <div
                className="pointer-events-none fixed top-6 left-1/2 -translate-x-1/2 z-[1000] flex flex-col items-center gap-3 w-full max-w-md"
                aria-live="polite"
                aria-atomic="true"
            >
                {toasts.map(({ id, title, message, variant }) => {
                    const v = VARIANTS[variant] ?? VARIANTS.info;
                    return (
                        <div
                            key={id}
                            className={`pointer-events-auto overflow-hidden rounded-xl shadow-lg ${v.base} w-full animate-toast-in`}
                        >
                            <div className={`flex items-start gap-3 p-3 ${v.text}`}>
                                <div className={`mt-0.5 shrink-0 ${v.text}`}>{v.icon}</div>
                                <div className="min-w-0 flex-1">
                                    {title && <div className="text-sm font-semibold">{title}</div>}
                                    {message && <div className="text-sm text-gray-600">{message}</div>}
                                </div>
                                <button
                                    onClick={() => remove(id)}
                                    className={`ml-1 rounded-md p-1 hover:opacity-80 transition ${v.text}`}
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* bottom pill accent */}
                            <div className="px-3 pb-3">
                                <span
                                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${v.pill}`}
                                >
                                    Notification
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* 🔹 Animation Keyframes */}
            <style>{`
                @keyframes toastIn {
                    0% { opacity: 0; transform: translateY(-20px) scale(0.98); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes toastOut {
                    0% { opacity: 1; transform: translateY(0) scale(1); }
                    100% { opacity: 0; transform: translateY(-10px) scale(0.98); }
                }
                .animate-toast-in {
                    animation: toastIn 0.3s ease-out;
                }
                .animate-toast-out {
                    animation: toastOut 0.3s ease-in forwards;
                }
            `}</style>
        </ToastContext.Provider>
    );
}
